import type { RunbookTask } from '@/types/entities'

/**
 * Calculates TaskOffset (minutes from runbook start) for runbook tasks based on dependencies and durations.
 * 
 * This function:
 * - Topologically sorts tasks by dependencies
 * - For each task, calculates TaskOffset (minutes from runbook start)
 * - TaskOffset = 0 for tasks with no dependencies
 * - TaskOffset = MAX(DependencyTaskOffset + DependencyDuration) for all dependencies
 * - Does NOT calculate absolute StartTime/FinishTime
 * - Handles circular dependencies gracefully
 * - Returns tasks with updated taskOffset
 * 
 * @param tasks - Array of runbook tasks to calculate offsets for
 * @returns Array of tasks with calculated taskOffset (in minutes)
 */
export function calculateRunbookTimes(tasks: RunbookTask[]): RunbookTask[] {
    
    // Step 1: Create a copy to avoid mutating the original
    const calculated = tasks.map(task => ({ ...task }))
    
    // Step 2: Build task map and dependency graph
    const taskMap = new Map<number, RunbookTask & { _finishOffsetMinutes?: number }>()
    const adjacencyList = new Map<number, number[]>() // taskNo → [dependent task numbers]
    const inDegree = new Map<number, number>() // taskNo → in-degree count
    
    // Initialize maps
    calculated.forEach((task) => {
        if (task.taskNo !== null && task.taskNo !== undefined) {
            // Ensure taskNo is a number for consistent map lookups
            const taskNo = typeof task.taskNo === 'number' ? task.taskNo : parseInt(String(task.taskNo), 10)
            if (!isNaN(taskNo)) {
                taskMap.set(taskNo, task)
                adjacencyList.set(taskNo, [])
                inDegree.set(taskNo, 0)
            }
        }
    })
    
    // Build dependency graph and calculate in-degrees
    calculated.forEach((task) => {
        if (task.taskNo === null) {
            task.taskOffset = null
            return
        }
        
        // Parse dependencies
        const depTaskNos = (task.dependencies || '')
            .split(',')
            .map(d => d.trim())
            .filter(d => d !== '')
            .map(d => parseInt(d, 10))
            .filter(d => !isNaN(d) && taskMap.has(d)) // Only include valid dependencies
        
        // Build reverse graph: for each dependency, add current task as dependent
        const taskNo = typeof task.taskNo === 'number' ? task.taskNo : parseInt(String(task.taskNo), 10)
        depTaskNos.forEach(depNo => {
            const dependents = adjacencyList.get(depNo) || []
            dependents.push(taskNo)
            adjacencyList.set(depNo, dependents)
        })
        
        // Set in-degree (number of dependencies)
        inDegree.set(taskNo, depTaskNos.length)
    })
    
    // Step 3: Topological sort using Kahn's algorithm
    const sortedTasks: number[] = []
    const queue: number[] = []
    
    // Start with tasks that have no dependencies (in-degree = 0)
    inDegree.forEach((degree, taskNo) => {
        if (degree === 0) {
            queue.push(taskNo)
        }
    })
    
    // Process queue
    while (queue.length > 0) {
        const taskNo = queue.shift()!
        sortedTasks.push(taskNo)
        
        // Process all dependents of this task
        const dependents = adjacencyList.get(taskNo) || []
        dependents.forEach(dependentNo => {
            const currentInDegree = inDegree.get(dependentNo) || 0
            const newInDegree = currentInDegree - 1
            inDegree.set(dependentNo, newInDegree)
            
            // If in-degree becomes 0, add to queue
            if (newInDegree === 0) {
                queue.push(dependentNo)
            }
        })
    }
    
    // Detect cycles: if sortedTasks.length < taskMap.size, there's a cycle
    if (sortedTasks.length < taskMap.size) {
        const unsortedTasks = Array.from(taskMap.keys()).filter(t => !sortedTasks.includes(t))
        console.warn(`Circular dependency detected involving tasks: ${unsortedTasks.join(', ')}`)
        
        // Add unsorted tasks to the end (they'll have incorrect offsets but won't break)
        unsortedTasks.forEach(taskNo => {
            if (!sortedTasks.includes(taskNo)) {
                sortedTasks.push(taskNo)
            }
        })
    }
    
    // Step 4: Forward pass - calculate TaskOffset in topological order
    sortedTasks.forEach(taskNo => {
        const task = taskMap.get(taskNo)
        if (!task) return
        
        // Parse dependencies (comma-separated task numbers as integers)
        const depTaskNos = (task.dependencies || '')
            .split(',')
            .map(d => d.trim())
            .filter(d => d !== '')
            .map(d => parseInt(d, 10))
            .filter(d => !isNaN(d) && Number.isInteger(d) && taskMap.has(d))
        
        // Calculate TaskOffset: MAX(DependencyTaskOffset + DependencyDuration) for all dependencies
        // If no dependencies, TaskOffset = 0
        let taskOffset = 0
        if (depTaskNos.length > 0) {
            depTaskNos.forEach(depNo => {
                const depTask = taskMap.get(depNo)
                if (depTask) {
                    // Dependencies should always have _finishOffsetMinutes set (processed before this task)
                    if (depTask._finishOffsetMinutes !== undefined) {
                        taskOffset = Math.max(taskOffset, depTask._finishOffsetMinutes)
                    } else {
                        // This shouldn't happen in topological order, but handle gracefully
                        console.warn(`Task ${taskNo} depends on task ${depNo} which hasn't been processed yet`)
                    }
                } else {
                    console.warn(`Task ${taskNo} depends on task ${depNo} which doesn't exist`)
                }
            })
        }
        
        // Handle tasks with no duration
        if (task.duration === null || task.duration === undefined) {
            task.taskOffset = taskOffset
            task._finishOffsetMinutes = taskOffset // Finish when dependencies finish
            return
        }
        
        // Calculate finish offset (TaskOffset + Duration)
        const finishOffsetMinutes = taskOffset + task.duration
        task._finishOffsetMinutes = finishOffsetMinutes
        
        // Set TaskOffset (in minutes)
        task.taskOffset = taskOffset
        
    })
    
    // Clean up temporary properties
    calculated.forEach(task => {
        if (task.taskNo !== null) {
            delete (task as any)._finishOffsetMinutes
        }
    })
    
    
    return calculated
}
